import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Modal from '../../components/ui/Modal';
import { Eye, CheckCircle, XCircle, Clock, AlertTriangle, Download } from 'lucide-react';

const statusFilters = [
  { value: '', label: 'All Status' }, { value: 'PENDING', label: 'Pending' }, { value: 'APPROVED', label: 'Approved' },
  { value: 'PROCESSING', label: 'Processing' }, { value: 'COMPLETED', label: 'Completed' }, { value: 'REJECTED', label: 'Rejected' },
];

export default function AdminWithdrawalsPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<any>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['adminWithdrawals', statusFilter, page],
    queryFn: () => api.get('/admin/withdrawals', { params: { status: statusFilter, page, limit: 20 } }).then(r => r.data),
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/admin/withdrawals/${id}/approve`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['adminWithdrawals'] }); setSelected(null); },
  });
  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.patch(`/admin/withdrawals/${id}/reject`, { reason }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['adminWithdrawals'] }); setSelected(null); setShowReject(false); setRejectReason(''); },
  });

  const withdrawals = data?.data || [];
  const meta = data?.meta || { total: 0, page: 1, totalPages: 1 };

  const statusCounts = {
    pending: withdrawals.filter((w: any) => w.status === 'PENDING').length,
    approved: withdrawals.filter((w: any) => w.status === 'APPROVED').length,
    completed: withdrawals.filter((w: any) => w.status === 'COMPLETED').length,
    rejected: withdrawals.filter((w: any) => w.status === 'REJECTED').length,
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-white">Withdrawal Management</h1><p className="text-sm text-slate-400">{meta.total} total withdrawals</p></div>
        <Button variant="secondary" size="sm"><Download className="w-4 h-4 mr-1" />Export</Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Pending', count: data?.pendingCount || 0, color: 'text-amber-400 bg-amber-500/10 border border-amber-500/20' },
          { label: 'Approved', count: data?.approvedCount || 0, color: 'text-primary-400 bg-primary-500/10 border border-primary-500/20' },
          { label: 'Completed', count: data?.completedCount || 0, color: 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/20' },
          { label: 'Rejected', count: data?.rejectedCount || 0, color: 'text-red-400 bg-red-500/10 border border-red-500/20' },
        ].map((s, i) => (
          <div key={i} className={`rounded-lg border p-3 ${s.color}`}>
            <p className="text-xs font-medium opacity-75">{s.label}</p>
            <p className="text-2xl font-bold mt-1">{s.count}</p>
          </div>
        ))}
      </div>

      <Card>
        <CardContent className="py-3">
          <div className="flex items-center gap-3">
            <Select options={statusFilters} value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="border-b border-white/[0.06] bg-white/[0.02]">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">User</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Amount</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Fee</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Net</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Method</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Actions</th>
              </tr></thead>
              <tbody className="divide-y divide-white/[0.04]">
                {isLoading ? <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">Loading...</td></tr> :
                  withdrawals.length === 0 ? <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">No withdrawals found</td></tr> :
                  withdrawals.map((w: any) => (
                    <tr key={w.id} className="hover:bg-white/[0.02]">
                      <td className="px-4 py-3"><p className="text-sm font-medium text-white">{w.user?.profile?.fullName || w.user?.username}</p><p className="text-xs text-slate-500">@{w.user?.username}</p></td>
                      <td className="px-4 py-3 text-sm font-medium text-white">{formatCurrency(w.amount)}</td>
                      <td className="px-4 py-3 text-sm text-slate-400">{formatCurrency(w.fee)}</td>
                      <td className="px-4 py-3 text-sm font-medium text-emerald-400">{formatCurrency(w.netAmount)}</td>
                      <td className="px-4 py-3 text-sm text-slate-300">{w.paymentMethod}</td>
                      <td className="px-4 py-3"><Badge status={w.status} /></td>
                      <td className="px-4 py-3 text-sm text-slate-400">{new Date(w.createdAt).toLocaleDateString()}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setSelected(w)}><Eye className="w-3.5 h-3.5" /></Button>
                          {w.status === 'PENDING' && <>
                            <Button variant="ghost" size="sm" onClick={() => approveMutation.mutate(w.id)}><CheckCircle className="w-3.5 h-3.5 text-emerald-400" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => { setSelected(w); setShowReject(true); }}><XCircle className="w-3.5 h-3.5 text-red-400" /></Button>
                          </>}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          {meta.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.06]">
              <p className="text-sm text-slate-400">Page {meta.page} of {meta.totalPages}</p>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                <Button variant="secondary" size="sm" disabled={page >= meta.totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Modal isOpen={!!selected && !showReject} onClose={() => setSelected(null)} title="Withdrawal Detail">
        {selected && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-white/[0.02] rounded-lg"><p className="text-slate-400 text-xs">Amount</p><p className="font-bold text-lg text-white">{formatCurrency(selected.amount)}</p></div>
              <div className="p-3 bg-white/[0.02] rounded-lg"><p className="text-slate-400 text-xs">Fee</p><p className="font-medium text-white">{formatCurrency(selected.fee)}</p></div>
              <div className="p-3 bg-white/[0.02] rounded-lg"><p className="text-slate-400 text-xs">Net Amount</p><p className="font-medium text-emerald-400">{formatCurrency(selected.netAmount)}</p></div>
              <div className="p-3 bg-white/[0.02] rounded-lg"><p className="text-slate-400 text-xs">Method</p><p className="font-medium text-white">{selected.paymentMethod}</p></div>
              <div className="p-3 bg-white/[0.02] rounded-lg"><p className="text-slate-400 text-xs">Status</p><Badge status={selected.status} /></div>
              <div className="p-3 bg-white/[0.02] rounded-lg"><p className="text-slate-400 text-xs">Requested</p><p className="font-medium text-white">{new Date(selected.createdAt).toLocaleString()}</p></div>
            </div>
            {selected.accountDetails && <div className="p-3 bg-white/[0.02] rounded-lg"><p className="text-slate-400 text-xs">Account Details</p><p className="font-medium text-white">{selected.accountDetails}</p></div>}
            {selected.status === 'PENDING' && (
              <div className="flex gap-2 pt-3 border-t border-white/[0.06]">
                <Button onClick={() => approveMutation.mutate(selected.id)} isLoading={approveMutation.isPending}><CheckCircle className="w-4 h-4 mr-1" />Approve</Button>
                <Button variant="danger" onClick={() => setShowReject(true)}><XCircle className="w-4 h-4 mr-1" />Reject</Button>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal isOpen={showReject} onClose={() => { setShowReject(false); setRejectReason(''); }} title="Reject Withdrawal">
        <div className="space-y-4">
          <p className="text-sm text-slate-300">Please provide a reason for rejecting this withdrawal.</p>
          <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3} placeholder="Rejection reason..."
            className="w-full px-3 py-2 border border-white/[0.06] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white/[0.02] text-white placeholder-slate-500" />
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => { setShowReject(false); setRejectReason(''); }}>Cancel</Button>
            <Button variant="danger" isLoading={rejectMutation.isPending} onClick={() => selected && rejectMutation.mutate({ id: selected.id, reason: rejectReason })}>Reject Withdrawal</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
